import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { projectService } from '../services/projectService';
import { Project, ProjectStatus, ProjectStatusLabels, ProjectPriorityLabels } from '../types/project';

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<ProjectStatus | ''>('');

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectService.getAll(
        filterStatus === '' ? undefined : filterStatus,
        searchTerm || undefined
      );
      setProjects(data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Projekte:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Projekte');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadProjects();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE');
  };

  const getStatusColor = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      [ProjectStatus.Geplant]: 'info',
      [ProjectStatus.Aktiv]: 'success',
      [ProjectStatus.Pausiert]: 'warning',
      [ProjectStatus.Abgeschlossen]: 'default',
      [ProjectStatus.Abgebrochen]: 'error',
    };
    return colors[status];
  };

  const getPriorityColor = (priority: number) => {
    const colors: Record<number, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      0: 'success',
      1: 'info',
      2: 'warning',
      3: 'error',
    };
    return colors[priority];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Projekte
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/projects/new')}
        >
          Neues Projekt
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Projekte durchsuchen"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value as ProjectStatus | '')}
              >
                <MenuItem value="">Alle Status</MenuItem>
                {Object.entries(ProjectStatusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
            >
              Suchen
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Project List */}
      {projects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Keine Projekte gefunden
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {projects.map((project) => (
            <Grid item xs={12} key={project.projectID}>
              <Card
                sx={{
                  cursor: 'pointer',
                  borderLeft: 4,
                  borderLeftColor: project.farbe || 'primary.main',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
                onClick={() => navigate(`/projects/${project.projectID}`)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box flex={1}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {project.name}
                      </Typography>
                      {project.beschreibung && (
                        <Typography color="text.secondary" paragraph>
                          {project.beschreibung}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(project.beginDatum)}
                        {project.endDatum && ` - ${formatDate(project.endDatum)}`}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1} alignItems="flex-end">
                      <Chip
                        label={ProjectStatusLabels[project.status]}
                        color={getStatusColor(project.status)}
                        size="small"
                      />
                      <Chip
                        label={`Priorität: ${ProjectPriorityLabels[project.prioritaet]}`}
                        color={getPriorityColor(project.prioritaet)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <DescriptionIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Dokumente: <strong>{project.anzahlDokumente}</strong>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AssignmentIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Tickets: <strong>{project.anzahlTickets}</strong>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Mails: <strong>{project.anzahlMails}</strong>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Team: <strong>{project.anzahlTeamMitglieder}</strong>
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Projects;
